// ============================================================
// CreditHoldWarning.js
// The one place the credit-hold warning is worded and styled.
//
// A customer on credit hold must be impossible to miss while anyone is working
// with them, but NOTHING here blocks anything: no disabled buttons, no refused
// saves. It is a warning, not a wall — the owner's decision.
//
// The message says WHAT TO DO, not just that something is wrong: this customer
// cannot use credit, payment is required by cash or card, and the reason the
// hold was placed. It appears on the customer profile, in the customer list, in
// the customer picker on a new document, on a document being created or edited,
// and on the invoice view — all from here, so the five cannot drift.
//
// ── COLOUR IS NOT THE MESSAGE ──
// The red (#A32D2D, the app's red token) is reinforcement. The words "CREDIT
// HOLD", the border and the bold weight carry it on their own, so it reads in
// black and white — on a monochrome printout, and for anyone who cannot
// distinguish the red.
// ============================================================

import React from 'react';

const RED = '#A32D2D';
const RED_TINT = '#FBECEC';

/** A missing creditHold field — every customer created before this — is false. */
export const isOnCreditHold = (customer) => customer?.creditHold === true;

/** What the person in front of the customer has to do about it. */
export const CREDIT_HOLD_ACTION = 'This customer cannot use credit. Payment is required by cash or card.';

/**
 * The full warning. `size` is 'banner' (profile, documents) or 'compact' (rows
 * in a list, where it sits beside other badges).
 *
 * Renders nothing when the customer is not on hold, so callers can drop it in
 * unconditionally.
 */
export default function CreditHoldWarning({ customer, size = 'banner', className = '', style = {} }) {
  if (!isOnCreditHold(customer)) return null;
  const reason = (customer.creditHoldReason || '').trim();

  if (size === 'compact') {
    return (
      <span
        className={className}
        title={reason ? `${CREDIT_HOLD_ACTION} Reason: ${reason}` : CREDIT_HOLD_ACTION}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          background: RED_TINT, color: RED, border: `1px solid ${RED}`,
          borderRadius: 6, padding: '2px 8px',
          fontSize: 10, fontWeight: 800, letterSpacing: 0.5, whiteSpace: 'nowrap',
          ...style,
        }}
      >
        ⛔ CREDIT HOLD — CASH OR CARD
      </span>
    );
  }

  return (
    <div
      className={className}
      role="alert"
      style={{
        background: RED_TINT, border: `2px solid ${RED}`, borderRadius: 12,
        padding: '12px 16px', color: RED, ...style,
      }}
    >
      <p style={{ margin: 0, fontSize: 14, fontWeight: 800, letterSpacing: 0.5 }}>
        ⛔ CREDIT HOLD
      </p>
      <p style={{ margin: '4px 0 0', fontSize: 14, fontWeight: 700 }}>
        {CREDIT_HOLD_ACTION}
      </p>
      {reason && (
        <p style={{ margin: '4px 0 0', fontSize: 13, fontWeight: 600 }}>
          Reason: {reason}
        </p>
      )}
    </div>
  );
}

/** Who placed the hold and when, for the profile. '' when not recorded. */
export function creditHoldBy(customer) {
  if (!isOnCreditHold(customer)) return '';
  const who = customer.creditHoldBy || 'unknown';
  const when = customer.creditHoldAt ? new Date(customer.creditHoldAt).toLocaleDateString('en-US') : '';
  return when ? `Placed by ${who} on ${when}` : `Placed by ${who}`;
}
